<template>
    <v-container class="pa-5" fluid>
        <SaveCuttingJobDialog
            :is-open="saveCuttingJobDialogOpen"
            :channels="channels"
            :papers="papers"
            :cutting-job="cuttingJobToEdit"
            @on-close="closeSaveCuttingJobDialog"
            @on-success="getCuttingJobs"
        />
        <ConfirmDialog
            :is-open="deleteCuttingJobDialog"
            :data="cuttingJobToDelete"
            removal
            @on-confirm="confirmDeleteCuttingJob"
            @on-abort="closeDeleteCuttingJobDialog"
        >
            <template #title>
                Usuwanie zlecenia docięcia
            </template>
            <template #content>
                Czy na pewno chcesz usunąć zlecenie docięcia nr
                <strong v-if="cuttingJobToDelete">
                    {{ cuttingJobToDelete.number }}
                </strong>
                ?
            </template>
            <template #confirmButton>
                Usuń
            </template>
        </ConfirmDialog>
        <page-title>Zlecenia docięcia</page-title>
        <v-row align="center">
            <v-col cols="12" sm="4" lg="4">
                <v-btn
                    color="secondary"
                    medium
                    data-sentry-label="Nowe zlecenie docięcia"
                    @click="showSaveCuttingJobDialog()"
                >
                    <v-icon left>
                        mdi-plus
                    </v-icon>
                    Nowe zlecenie
                </v-btn>
            </v-col>
            <v-col cols="12" sm="8" lg="8">
                <v-container fluid class="pa-0">
                    <v-row justify="end" dense>
                        <v-col cols="12" md="6">
                            <v-select
                                v-model="filters.status_id"
                                label="Status"
                                :items="statuses"
                                item-value="id"
                                item-text="name"
                                no-data-text="Nie znaleziono statusu"
                                solo
                                clearable
                                hide-details
                                @change="changeFilter"
                                @click:clear="clearFilter('status_id')"
                            />
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-autocomplete
                                v-model="filters.paper_id"
                                label="Rodzaj surowca"
                                :items="papers"
                                item-value="id"
                                item-text="name"
                                no-data-text="Nie znaleziono papieru"
                                solo
                                clearable
                                hide-details
                                @change="changeFilter"
                                @click:clear="clearFilter('paper_id')"
                            />
                        </v-col>
                    </v-row>
                    <v-row justify="end" dense>
                        <v-col cols="12" md="6">
                            <v-select
                                v-model="filters.channel_id"
                                label="Kanał"
                                :items="channels"
                                item-value="id"
                                item-text="name"
                                no-data-text="Nie znaleziono kanału"
                                solo
                                clearable
                                hide-details
                                @change="changeFilter"
                                @click:clear="clearFilter('channel_id')"
                            />
                        </v-col>
                    </v-row>
                    <v-row justify="end" dense>
                        <v-col cols="12" md="4">
                            <v-menu
                                v-model="creationDateMenuFrom"
                                :close-on-content-click="false"
                                :nudge-right="40"
                                transition="scale-transition"
                                offset-y
                                min-width="290px"
                            >
                                <template #activator="{on}">
                                    <v-text-field
                                        v-model="filters.createdAt__gte"
                                        label="Zlecenie utworzone od"
                                        prepend-icon="mdi-calendar"
                                        readonly
                                        clearable
                                        solo
                                        hide-details
                                        v-on="on"
                                        @input="changeFilter"
                                        @click:clear="clearFilter('createdAt__gte')"
                                    />
                                </template>
                                <v-date-picker
                                    v-model="filters.createdAt__gte"
                                    first-day-of-week="1"
                                    @change="changeFilter"
                                    @input="creationDateMenuFrom = false"
                                />
                            </v-menu>
                        </v-col>
                        <v-col cols="12" md="4">
                            <v-menu
                                v-model="creationDateMenuTo"
                                :close-on-content-click="false"
                                :nudge-right="40"
                                transition="scale-transition"
                                offset-y
                                min-width="290px"
                            >
                                <template #activator="{on}">
                                    <v-text-field
                                        v-model="filters.createdAt__lte"
                                        label="Zlecenie utworzone do"
                                        prepend-icon="mdi-calendar"
                                        readonly
                                        clearable
                                        solo
                                        hide-details
                                        v-on="on"
                                        @input="changeFilter"
                                        @click:clear="clearFilter('createdAt__lte')"
                                    />
                                </template>
                                <v-date-picker
                                    v-model="filters.createdAt__lte"
                                    first-day-of-week="1"
                                    @change="changeFilter"
                                    @input="creationDateMenuTo = false"
                                />
                            </v-menu>
                        </v-col>
                    </v-row>
                    <v-row justify="end" no-gutters>
                        <v-col cols="auto" class="mt-4">
                            <v-btn
                                text
                                x-small
                                data-sentry-label="Generuj raport"
                                :disabled="cuttingJobsLoading"
                                @click="generateCuttingJobsReport"
                            >
                                <v-icon
                                    v-if="!cuttingJobsLoading"
                                    small
                                    color="green"
                                >
                                    mdi-file-excel
                                </v-icon>
                                <v-progress-circular
                                    v-else
                                    indeterminate
                                    color="primary"
                                    class="mr-1"
                                    size="12"
                                    width="2"
                                />
                                Generuj raport
                            </v-btn>
                        </v-col>
                    </v-row>
                </v-container>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12">
                <v-card>
                    <v-data-table
                        :headers="headers"
                        :items="cuttingJobs"
                        :loading="cuttingJobsLoading"
                        :footer-props="footerProps"
                        :server-items-length="pagination.itemsLength"
                        :page.sync="pagination.page"
                        :items-per-page.sync="pagination.itemsPerPage"
                        :mobile-breakpoint="0"
                        @pagination="updatePagination"
                    >
                        <template #item.createdAt="{item}">
                            {{ formatDate(item.createdAt) }}
                        </template>
                        <template #item.channel="{item}">
                            {{ item.channel.name }}
                        </template>
                        <template #item.paper="{item}">
                            {{ item.paper.name }}
                        </template>
                        <template #item.inputFormat="{item}">
                            {{ item.inputFormatA }}x{{ item.inputFormatB }}
                        </template>
                        <template #item.outputFormat="{item}">
                            {{ item.outputFormatA }}x{{ item.outputFormatB }}
                        </template>
                        <template #item.inputSheetsCount="{item}">
                            {{ parseEmptyValue(item.inputSheetsCount) }}
                        </template>
                        <template #item.inputSheetsWeight="{item}">
                            {{ parseEmptyValue(item.inputSheetsWeight) }}
                        </template>
                        <template #item.outputSheetsWeight="{item}">
                            {{ parseEmptyValue(item.outputSheetsWeight) }}
                        </template>
                        <template #item.wasteSheetsWeight="{item}">
                            {{ parseEmptyValue(item.wasteSheetsWeight) }}
                        </template>
                        <template #item.estimatedTime="{item}">
                            {{ parseEmptyValue(item.estimatedTime) }}
                        </template>
                        <template #item.status="{item}">
                            {{ item.status.name }}
                        </template>
                        <template #item.actions="{item}">
                            <v-row>
                                <div class="d-inline-flex">
                                    <v-tooltip :open-delay="tooltipDelay" top>
                                        <template #activator="{on}">
                                            <v-btn
                                                icon
                                                data-sentry-label="Edytuj"
                                                :disabled="isCuttingJobEditDisabled(item)"
                                                v-on="on"
                                                @click="handleEditCuttingJob(item)"
                                            >
                                                <v-icon color="green">
                                                    mdi-pencil
                                                </v-icon>
                                            </v-btn>
                                        </template>
                                        <span>Edytuj</span>
                                    </v-tooltip>
                                </div>
                                <v-row>
                                    <div class="d-inline-flex">
                                        <v-tooltip :open-delay="tooltipDelay" top>
                                            <template #activator="{on}">
                                                <v-btn
                                                    icon
                                                    data-sentry-label="Wygeneruj kartę PDF"
                                                    v-on="on"
                                                    @click="cuttingJobCardPrint(item)"
                                                >
                                                    <v-icon color="primary">
                                                        mdi-printer
                                                    </v-icon>
                                                </v-btn>
                                            </template>
                                            <span>Wygeneruj kartę PDF</span>
                                        </v-tooltip>
                                    </div>
                                </v-row>
                                <div class="d-inline-flex">
                                    <v-tooltip :open-delay="tooltipDelay" top>
                                        <template #activator="{on}">
                                            <v-btn
                                                icon
                                                data-sentry-label="Usuń"
                                                :disabled="isCuttingJobDeleteDisabled(item)"
                                                v-on="on"
                                                @click="handleDeleteCuttingJob(item)"
                                            >
                                                <v-icon color="pink">
                                                    mdi-delete
                                                </v-icon>
                                            </v-btn>
                                        </template>
                                        <span>Usuń</span>
                                    </v-tooltip>
                                </div>
                            </v-row>
                        </template>
                    </v-data-table>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import PageTitle from '@/components/common/PageTitle'
import ConfirmDialog from '@/components/common/ConfirmDialog'
import { errorHandler } from '@/utils/errorHandler'
import dateFormatter from '@/mixins/dateFormatter'
import pagination from '@/mixins/dataTablePagination'
import parse from '@/mixins/parse'
import tooltipDelay from '@/mixins/tooltip'
import { openPdfFile } from '@/utils/pdfHandler'
import filters from '@/mixins/dataTableFilters'
import SaveCuttingJobDialog from './Dialogs/SaveCuttingJobDialog'

export default {
    name: 'CuttingJobs',
    components: {
        PageTitle,
        ConfirmDialog,
        SaveCuttingJobDialog,
    },
    mixins: [
        pagination,
        dateFormatter,
        tooltipDelay,
        filters,
        parse
    ],
    data() {
        return {
            creationDateMenuFrom: false,
            creationDateMenuTo: false,
            saveCuttingJobDialogOpen: false,
            deleteCuttingJobDialog: false,
            cuttingJobToDelete: null,
            cuttingJobToEdit: null,
            headers: [
                {
                    text: 'Data',
                    value: 'createdAt',
                    sortable: false
                },
                {
                    text: 'Kanał',
                    value: 'channel',
                    sortable: false
                },
                {
                    text: 'Numer zlecenia docięcia',
                    value: 'number',
                    sortable: false
                },
                {
                    text: 'Numer zlecenia',
                    value: 'objectNumber',
                    sortable: false
                },
                {
                    text: 'Rodzaj surowca',
                    value: 'paper',
                    sortable: false
                },
                {
                    text: 'Format wejściowy surowca',
                    value: 'inputFormat',
                    sortable: false
                },
                {
                    text: 'Format wyjściowy surowca',
                    value: 'outputFormat',
                    sortable: false
                },
                {
                    text: 'Ilość arkuszy na wejściu',
                    value: 'inputSheetsCount',
                    sortable: false
                },
                {
                    text: 'Ilość użytków po krojeniu',
                    value: 'partsCount',
                    sortable: false
                },
                {
                    text: 'Waga arkuszy na wejściu',
                    value: 'inputSheetsWeight',
                    sortable: false
                },
                {
                    text: 'Waga arkuszy na wyjściu',
                    value: 'outputSheetsWeight',
                    sortable: false
                },
                {
                    text: 'Ilość arkuszy na wyjściu',
                    value: 'outputSheetsCount',
                    sortable: false
                },
                {
                    text: 'Waga odpadu',
                    value: 'wasteSheetsWeight',
                    sortable: false
                },
                {
                    text: 'Szacowany czas ',
                    value: 'estimatedTime',
                    sortable: false
                },
                {
                    text: 'Status',
                    value: 'status',
                    sortable: false
                },
                {
                    text: 'Akcje',
                    value: 'actions',
                    sortable: false,
                    align: 'center',
                    width: 100
                }
            ]
        }
    },
    computed: {
        userEmail() {
            return this.$store.state.user.userEmail
        },
        cuttingJobs() {
            return this.$store.state.cuttingJobs.cuttingJobs.cuttingJobs
        },
        cuttingJobsLoading() {
            return this.$store.state.cuttingJobs.cuttingJobs.cuttingJobsLoading
        },
        statuses() {
            return this.$store.state.cuttingJobs.cuttingJobs.statuses
        },
        papers() {
            return this.$store.state.configurations.papers.papers
        },
        channels() {
            return this.$store.state.cuttingJobs.cuttingJobs.channels
        },
        filterQuery() {
            const filters = Object.assign({}, this.filters)
            let query = ''

            for (let item in filters) {
                if (filters[item] !== null && typeof filters[item] !== 'undefined') {
                    query = query.concat('&', `${item}=${filters[item]}`)
                }
            }

            return query
        }
    },
    mounted() {
        Promise.all([
            this.$store.dispatch('cuttingJobs/getStatuses'),
            this.$store.dispatch('configurations/getPapers', {
                query: '?isVisibleInCuttingJob=true'
            }),
            this.$store.dispatch('cuttingJobs/getChannels')
        ])
            .catch(error => {
                errorHandler(error)
            })

        this.getCuttingJobs()
    },
    methods: { 
        isCuttingJobEditDisabled(item) {
            if (item.statusId === 10) { // status w realizacji
                return false
            }

            return true
        },
        async handleEditCuttingJob(item) {
            try {
                await this.$store.dispatch('cuttingJobs/canEditCuttingJob', {
                    id: item.id
                })

                this.showSaveCuttingJobDialog(item)
            } catch {
                return
            }
        },
        isCuttingJobDeleteDisabled(item) {
            if (item.statusId === 10) { // status w realizacji
                return false
            }

            return true
        },
        async handleDeleteCuttingJob(item) {
            try {
                await this.$store.dispatch('cuttingJobs/canDeleteCuttingJob', {
                    id: item.id
                })

                this.cuttingJobToDelete = item
                this.deleteCuttingJobDialog = true
            } catch {
                return
            }
        },
        getCuttingJobs() {
            this.$store.dispatch('cuttingJobs/getCuttingJobs', {
                paginate: this.paginate(),
                query: this.filterQuery
            })
                .then(response => {
                    this.handlePaginationResponse(response)
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        generateCuttingJobsReport() {
            this.$store.dispatch('cuttingJobs/generateReport', {
                paginate: this.paginate(),
                query: this.filterQuery,
                userEmail: this.userEmail
            })
                .catch(error => {
                    errorHandler(error)
                })
        },
        updatePagination(pagination) {
            this.pagination = pagination
            this.evaluateSavedPagination(this.$options.name)
            if (pagination.pageCount !== 0 && !this.paginationSettings.paginationPending && !this.savedPagination.pagination) {
                this.getCuttingJobs()
            }
            this.clearPaginationPending()
        },
        cuttingJobCardPrint(cuttingJob) {
            this.$store.dispatch('cuttingJobs/cuttingJobCardPrint', {
                cuttingJobId: cuttingJob.id,
                templateCode: 'cutting_job_card_print'
            })
                .then(response => {
                    openPdfFile(response.data)
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        changeFilter() {
            this.resetPagination()
            this.getCuttingJobs()
        },
        showSaveCuttingJobDialog(cuttingJob = null) {
            this.cuttingJobToEdit = cuttingJob
            this.saveCuttingJobDialogOpen = true
        },
        closeSaveCuttingJobDialog() {
            this.saveCuttingJobDialogOpen = false
            this.cuttingJobToEdit = null
        },
        closeDeleteCuttingJobDialog() {
            this.deleteCuttingJobDialog = false
            this.cuttingJobToDelete = null
        },
        async confirmDeleteCuttingJob(cuttingJob) {
            try {
                await this.$store.dispatch('cuttingJobs/canDeleteCuttingJob', {
                    id: cuttingJob.id
                })
                await this.$store.dispatch('cuttingJobs/deleteCuttingJob', {
                    id: cuttingJob.id,
                })
                this.getCuttingJobs()
                this.closeDeleteCuttingJobDialog()
            } catch {
                return
            }

        },
    },
}
</script>
